import React, { useState, useEffect } from 'react';
import { Button, Icon, Intent, Text, Tooltip } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { ZipApi, OpalTool, OpalInstance, OpalToolStatus } from '../../../lib/ZipApi';
import { directoryStore } from '../../../stores/DirectoryStore';
import { showToasts } from '../../../lib/toaster';
import { AppDetailStore } from '../../../stores/AppDetailStore';
import styles from './Opal.module.sass';
import { handleExpiredOptiAccessToken } from './opalUtils';
import { getApiErrorMessage } from '../../../lib/ZipApiError';

interface Props {
  tool: OpalTool;
  instance: OpalInstance;
  detailStore: AppDetailStore;
  onToolUpdated: () => void;
}

const TOOL_NOT_READY_MESSAGE = 'Tool is not ready. Configure the tool in the Settings tab';
const TOOL_READY_CHECK_FAILED = 'Readiness check failed';

export const OpalToolRow = ({ tool, instance, detailStore, onToolUpdated }: Props) => {
  const [readyResponse, setReadyResponse] = useState<{ ready: boolean; reason?: string } | null>(null);
  const [isReadyLoading, setIsReadyLoading] = useState(false);
  const [isOperationLoading, setIsOperationLoading] = useState(false);
  const [isSyncLoading, setIsSyncLoading] = useState(false);

  const isToolRegistered = () => tool.status === OpalToolStatus.REGISTERED;
  const isRegisteredByAnother = () => tool.status === OpalToolStatus.REGISTERED_BY_ANOTHER;

  const checkToolReady = async (toolToCheck: OpalTool): Promise<{ ready: boolean; reason?: string }> => {
    try {
      const readyUrl = toolToCheck.discovery_url.replace(/\/discovery$/, '/ready');
      const response = await fetch(readyUrl);

      if (!response.ok) {
        return { ready: false, reason: TOOL_READY_CHECK_FAILED };
      }

      const result = await response.json() as { ready: boolean; reason?: string };
      if (result.ready) {
        return { ready: true };
      } else {
        return {
          ready: false,
          reason: result.reason || TOOL_NOT_READY_MESSAGE
        };
      }
    } catch (error) {
      console.error(`Error checking ready status for tool ${toolToCheck.id}:`, error);
      return { ready: false, reason: TOOL_READY_CHECK_FAILED };
    }
  };

  const checkToolReadyAsync = async () => {
    setIsReadyLoading(true);
    try {
      const result = await checkToolReady(tool);
      setReadyResponse(result);
    } catch (error) {
      setReadyResponse({ ready: false, reason: TOOL_READY_CHECK_FAILED });
    } finally {
      setIsReadyLoading(false);
    }
  };

  useEffect(() => {
    checkToolReadyAsync();
  }, [tool.id]);

  const handleToggleRegistration = async () => {
    if (!directoryStore.trackerId || !detailStore.installId || isOperationLoading) {
      return;
    }

    setIsOperationLoading(true);

    try {
      const optiidToken = directoryStore.getOptiAccessToken();
      if (isToolRegistered()) {
        await ZipApi.unregisterOpalTool(
          directoryStore.trackerId!,
          instance.InstanceId,
          tool.id,
          optiidToken
        );
      } else {
        await ZipApi.registerOpalTool(
          directoryStore.trackerId!,
          instance.InstanceId,
          tool.name,
          detailStore.installId!,
          optiidToken
        );
      }
      setIsReadyLoading(true);
      onToolUpdated();
    } catch (error) {
      if (!handleExpiredOptiAccessToken(error)) {
        const errorMessage = await getApiErrorMessage(error, 'Error with opal registration');
        showToasts([{intent: Intent.DANGER, message: errorMessage}]);
      }
    } finally {
      setIsOperationLoading(false);
    }
  };

  const handleSyncTool = async () => {
    if (!directoryStore.trackerId || !detailStore.installId || isSyncLoading) {
      return;
    }

    setIsSyncLoading(true);

    try {
      const optiidToken = directoryStore.getOptiAccessToken();
      await ZipApi.syncOpalTool(
        directoryStore.trackerId!,
        instance.InstanceId,
        tool.id,
        optiidToken
      );
      showToasts([{intent: 'success', message: 'Tool synced successfully'}]);
    } catch (error) {
      if (!handleExpiredOptiAccessToken(error)) {
        const errorMessage = await getApiErrorMessage(error, 'Error syncing tool');
        showToasts([{intent: Intent.DANGER, message: errorMessage}]);
      }
    } finally {
      setIsSyncLoading(false);
    }
  };

  const getActionButtons = () => {
    const toolReady = readyResponse?.ready ?? false;

    if (isReadyLoading) {
      return (
        <div className={styles.actionButtons}>
          <Icon icon={IconNames.REFRESH} intent={Intent.NONE} />
          <Text style={{ marginLeft: '8px' }}>Checking configuration...</Text>
        </div>
      );
    }

    if (readyResponse && !toolReady) {
      const reason = readyResponse.reason ?? TOOL_NOT_READY_MESSAGE;
      return (
        <div className={styles.actionButtons}>
          <Text>{reason}</Text>
        </div>
      );
    }

    if (isRegisteredByAnother()) {
      return (
        <div className={styles.actionButtons}>
          <Text>Already registered from another OCP account.</Text>
        </div>
      );
    }

    const handleRegisterClick = () => {
      if (!isOperationLoading && !isSyncLoading) {
        handleToggleRegistration();
      }
    };

    const handleSyncClick = () => {
      if (!isSyncLoading && !isOperationLoading) {
        handleSyncTool();
      }
    };

    return (
      <div className={styles.actionButtons}>
        <Button
          text={isOperationLoading
            ? (isToolRegistered() ? 'Removing...' : 'Adding...')
            : (isToolRegistered() ? 'Remove from Opal' : 'Add to Opal')
          }
          intent={isToolRegistered() ? Intent.WARNING : Intent.SUCCESS}
          icon={isToolRegistered() ? IconNames.STOP : IconNames.PLAY}
          onClick={handleRegisterClick}
          small
          loading={isOperationLoading}
          disabled={isOperationLoading || isSyncLoading}
        />
        {isToolRegistered() && (
          <Button
            text={isSyncLoading ? 'Syncing...' : 'Sync'}
            intent={Intent.PRIMARY}
            icon={IconNames.REFRESH}
            onClick={handleSyncClick}
            small
            loading={isSyncLoading}
            disabled={isOperationLoading || isSyncLoading}
          />
        )}
      </div>
    );
  };

  const getStatusIcon = () => {
    const toolReady = readyResponse?.ready ?? false;
    if (isReadyLoading) {
      return (
        <Tooltip content="Checking tool readiness...">
          <Icon
            icon={IconNames.REFRESH}
            intent={Intent.NONE}
            className={styles.statusIcon}
          />
        </Tooltip>
      );
    }

    if (readyResponse && !toolReady) {
      const reason = readyResponse.reason ?? TOOL_NOT_READY_MESSAGE;
      return (
        <Tooltip content={reason}>
          <Icon
            icon={IconNames.WARNING_SIGN}
            intent={Intent.WARNING}
            className={styles.statusIcon}
          />
        </Tooltip>
      );
    }

    if (toolReady) {
      return (
        <Icon
          icon={isToolRegistered() ? IconNames.TICK_CIRCLE : IconNames.PAUSE}
          intent={isToolRegistered() ? Intent.SUCCESS : Intent.NONE}
          className={styles.statusIcon}
        />
      );
    }

    return (
      <Tooltip content="Checking tool readiness...">
        <Icon
          icon={IconNames.REFRESH}
          intent={Intent.NONE}
          className={styles.statusIcon}
        />
      </Tooltip>
    );
  };

  const isReady = readyResponse?.ready ?? false;
  const toolKey = `${instance.InstanceId}-${tool.name}`;

  return (
    <tr
      key={toolKey}
      className={!isReady ? styles.disabledRow : ''}
    >
      <td>
        <div className={styles.instanceName}>
          {getStatusIcon()}
          <Text>{instance.InstanceName}</Text>
        </div>
      </td>
      <td>
        {getActionButtons()}
      </td>
    </tr>
  );
};
